import { motion } from "framer-motion";
import { Compass, Crown, Hammer, Users } from "lucide-react";
import { useContent } from "@/content/ContentContext";

const EASE = [0.16, 1, 0.3, 1];
const ICONS = [Compass, Crown, Hammer, Users];

export default function FourPillars() {
  const { content } = useContent();
  const p = content.pillars;

  return (
    <section id="pillars" className="py-24 sm:py-32 bg-[#0A0A0C] border-y border-[rgba(212,175,55,0.14)]" data-testid="pillars-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: EASE }}
          className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6"
        >
          <div>
            <p className="font-mono-lux text-xs uppercase tracking-[0.3em] text-gold mb-5" data-testid="pillars-eyebrow">
              {p.eyebrow}
            </p>
            <h2 className="font-serif-lux text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#F7F7F8]" data-testid="pillars-heading">
              {p.headingPre} <br className="hidden sm:block" />
              <span className="italic text-gold-champagne">{p.headingAccent}</span>
            </h2>
          </div>
          <p className="max-w-sm text-sm text-[#757582] leading-relaxed">
            {p.sub}
          </p>
        </motion.div>

        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {p.items.map((item, i) => {
            const Icon = ICONS[i % ICONS.length];
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 36 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.8, ease: EASE, delay: i * 0.12 }}
                whileHover={{ y: -8 }}
                className="group relative bg-surface border border-[rgba(212,175,55,0.16)] p-8 hover:border-[rgba(212,175,55,0.5)] transition-colors duration-500"
                data-testid={`pillar-card-${(item.name || `pillar-${i}`).toLowerCase()}`}
              >
                <Icon
                  size={30}
                  strokeWidth={1.25}
                  className="text-gold group-hover:text-gold-bright transition-colors duration-500"
                  aria-hidden="true"
                />
                <p className="mt-6 font-mono-lux text-[11px] uppercase tracking-[0.25em] text-gold">{item.name}</p>
                <h3 className="mt-3 font-serif-lux text-xl sm:text-2xl text-[#F7F7F8] tracking-tight leading-snug">{item.title}</h3>
                <p className="mt-4 text-sm text-[#A0A0AD] leading-relaxed">{item.desc}</p>
                <span aria-hidden="true" className="absolute bottom-0 left-0 h-[2px] w-0 bg-gold group-hover:w-full transition-[width] duration-700 ease-out" />
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
